import { useCallback, useMemo, useState, useEffect } from "react";
import {
  TextField,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  TablePagination,
  MenuItem,
  Checkbox,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import CloseIcon from "@mui/icons-material/Close";

const CasesListTable = ({ data, onRemoveCase }) => {
  // Destructure Districts along with users
  const { users, Districts, Languages } = useSelector((state) => state.user || {});

  const telecallers = useMemo(() => {
    const tc = users.filter((u) =>
      u.roles.includes(process.env.REACT_APP_TELECALLER),
    );
    return tc.length > 0 ? tc : [];
  }, [users]);

  const clusters = useMemo(() => {
    return Languages.map((language) => ({
      _id: language._id,
      name: language.name,
    }));
  }, [Languages]);

  const getTelecaller = useCallback(
    (userId) => {
      let value = telecallers.find((u) => u._id === userId);
      return value?.name || "Any";
    },
    [telecallers],
  );

  // Helper function to get district name by ID
  const getDistrictName = useCallback(
    (districtId) => {
      if (!Districts || Districts.length === 0) return "N/A"; // Handle case where Districts might not be loaded yet
      const district = Districts.find((d) => d._id === districtId);
      return district?.name || "Unknown"; // Return name or 'Unknown' if not found
    },
    [Districts], // Dependency array includes Districts
  );

  // Helper function to get the cluster name by ID, the case might have clusterId
  const getClusterName = useCallback(
    (clusterId) => {
      if (!clusters || clusters.length === 0) return "N/A";
      let value = clusters.find((c) => c._id === clusterId);
      return value?.name || "N/A";
    },
    [clusters],
  );

  const [casesMap, setCasesMap] = useState(new Map(data));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [allocatedFilter, setAllocatedFilter] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Map());
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  useEffect(() => {
    setCasesMap(new Map(data));
  }, [data])

  const filterSearch = (e) => {
    let value = e.target.value.toLowerCase();
    if (value.length === 0) {
      setCasesMap(new Map(data));
      return;
    }

    let filtered = Array.from(casesMap.values()).filter((selectedCase) => {
      const nameMatch = selectedCase.name.toLowerCase().includes(value);
      const motherIDMatch =
        selectedCase.motherID &&
        selectedCase.motherID.toLowerCase().includes(value);
      return nameMatch || motherIDMatch;
    });

    let newMap = new Map();
    filtered.forEach((selectedCase) => {
      newMap.set(selectedCase["_id"], selectedCase);
    });
    setCasesMap(newMap);
    setPage(0);
  };

  const handleRemoveSelectedCase = (id, motherID) => {
    onRemoveCase(id, motherID);
    setCasesMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.delete(id);
      return newMap;
    });
    setSelectedRows((prevSelected) => {
      const newSelected = new Map(prevSelected);
      newSelected.delete(id);
      return newSelected;
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAllocatedFilterChange = (event) => {
    setAllocatedFilter(event.target.value);
    filterCasesByAllocation(event.target.value);
  };

  const filterCasesByAllocation = (allocatedUserId) => {
    if (!allocatedUserId) {
      setCasesMap(new Map(data));
      return;
    }

    let filtered = Array.from(data.values()).filter(
      (selectedCase) => selectedCase.allocatedToUser === allocatedUserId,
    );

    let newMap = new Map();
    filtered.forEach((selectedCase) => {
      newMap.set(selectedCase["_id"], selectedCase);
    });
    setCasesMap(newMap);
    setPage(0);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = new Map();
      paginatedCases.forEach((n) => newSelected.set(n._id, n));
      setSelectedRows(newSelected);
    } else {
      setSelectedRows(new Map());
    }
  };

  const handleClick = (event, id) => {
    setSelectedRows((prevSelected) => {
      const newSelected = new Map(prevSelected);
      if (newSelected.has(id)) {
        newSelected.delete(id);
      } else {
        newSelected.set(id, casesMap.get(id));
      }
      return newSelected;
    });
  };

  const isSelected = (id) => selectedRows.has(id);

  const handleRemoveSelected = () => {
    selectedRows.forEach((_, id) => handleRemoveSelectedCase(id, selectedRows.get(id).motherID));
    setSelectedRows(new Map());
  };

  const handleDateFilterChange = (newValue, type) => {
    let filtered = Array.from(data.values()).filter((selectedCase) => {
      if (!selectedCase.callDate) return false;
      const caseDate = dayjs(selectedCase.callDate);

      let fromDateObj = null;
      let toDateObj = null;

      if (type === "from-date") {
        fromDateObj = dayjs(newValue.$d);
      } else if (type === "to-date") {
        toDateObj = dayjs(newValue.$d);
      }
      if (fromDateObj && toDateObj) {
        return (
          (caseDate.isAfter(fromDateObj) || caseDate.isSame(fromDateObj)) &&
          (caseDate.isBefore(toDateObj) || caseDate.isSame(toDateObj))
        );
      } else if (fromDateObj) {
        return caseDate.isAfter(fromDateObj) || caseDate.isSame(fromDateObj);
      } else if (toDateObj) {
        return caseDate.isBefore(toDateObj) || caseDate.isSame(toDateObj);
      }
      return true;
    });

    let newMap = new Map();
    filtered.forEach((selectedCase) => {
      newMap.set(selectedCase["_id"], selectedCase);
    });
    setCasesMap(newMap);
    setPage(0);
  };

  const paginatedCases = Array.from(casesMap.values()).slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction={"column"} spacing={2} maxHeight={"600px"}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="filters-content"
            id="filters-header"
          >
            <Typography>Filters & Actions</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              direction={{ xs: "column", md: "row" }} // Responsive direction
              spacing={2}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }} // Align items differently on small screens
              width="100%" // Ensure stack takes full width
            >
              <Stack
                direction={{ xs: "column", sm: "row" }} // Inner stack also responsive
                spacing={2}
                flexWrap="wrap" // Allow wrapping
                flexGrow={1} // Allow inner stack to grow
              >
                <TextField
                  label="Search by name or mother ID"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  onChange={(e) => {
                    filterSearch(e);
                  }}
                  sx={{ flexGrow: 1, minWidth: '150px' }} // Allow text field to grow
                />
                <TextField
                  select
                  label="Filter by Allocated To"
                  value={allocatedFilter}
                  onChange={handleAllocatedFilterChange}
                  sx={{ minWidth: 200 }}
                >
                  <MenuItem value="">
                    <em>All</em>
                  </MenuItem>
                  {telecallers.map((telecaller) => (
                    <MenuItem key={telecaller._id} value={telecaller._id}>
                      {telecaller.name}
                    </MenuItem>
                  ))}
                </TextField>
                <DatePicker
                  label="From Date"
                  value={fromDate}
                  onChange={(newValue) => {
                    setFromDate(newValue);
                    handleDateFilterChange(newValue, "from-date");
                  }}
                  sx={{ minWidth: '150px' }}
                />
                <DatePicker
                  label="To Date"
                  value={toDate}
                  onChange={(newValue) => {
                    setToDate(newValue);
                    handleDateFilterChange(newValue, "to-date");
                  }}
                  sx={{ minWidth: '150px' }}
                />
              </Stack>
              <Button
                variant="contained"
                color="secondary"
                onClick={handleRemoveSelected}
                disabled={selectedRows.size === 0}
                sx={{ mt: { xs: 2, md: 0 }, alignSelf: { xs: 'flex-end', md: 'center' } }} // Margin top on small screens, align differently
              >
                Remove Selected
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table stickyHeader aria-label="selected cases table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={
                      selectedRows.size > 0 &&
                      selectedRows.size < paginatedCases.length
                    }
                    checked={
                      paginatedCases.length > 0 &&
                      selectedRows.size === paginatedCases.length
                    }
                    onChange={handleSelectAllClick}
                    inputProps={{
                      "aria-label": "select all cases",
                    }}
                  />
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Mother ID</TableCell>
                <TableCell>Cluster</TableCell>
                <TableCell>District</TableCell> {/* Added District Header */}
                <TableCell>Allocated To</TableCell>
                <TableCell>Assigned On</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedCases.map((selectedCase) => {
                const isItemSelected = isSelected(selectedCase._id);
                return (
                  <TableRow
                    hover
                    onClick={(event) => handleClick(event, selectedCase._id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={selectedCase._id}
                    selected={isItemSelected}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={isItemSelected}
                        inputProps={{
                          "aria-labelledby": selectedCase._id,
                        }}
                      />
                    </TableCell>
                    <TableCell>{selectedCase.name}</TableCell>
                    <TableCell>{selectedCase.motherID}</TableCell>
                    {/* Cluster Name */}
                    <TableCell>{getClusterName(selectedCase.clusterId)}</TableCell>
                    {/* Display District Name */}
                    <TableCell>{getDistrictName(selectedCase.districtId)}</TableCell>
                    <TableCell>
                      {getTelecaller(selectedCase?.allocatedToUser)}
                    </TableCell>
                    <TableCell>
                      {selectedCase.callDate
                        ? dayjs(selectedCase.callDate).format("DD-MM-YYYY")
                        : ""}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Remove this selection">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveSelectedCase(
                              selectedCase._id,
                              selectedCase.motherID,
                            );
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[30]}
          component="div"
          count={casesMap.size}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Stack>
    </LocalizationProvider>
  );
};

export default CasesListTable;
